import React, { useMemo } from "react";
import { useRef, useState } from "react";

import EditIcon from "@uui/Edit02";
import DeleteIcon from "@uui/Trash01";
import { HTMLInputElement } from "happy-dom";
import { isEmpty, reject, trim } from "lodash/fp";

import { Divider, Typography } from "@mui/material";

import Modal, { ModalCloseButton } from "@app/analytics/components/Modal";
import {
  SUPPRESSED_SPEAKER_NAMES,
  UNKNOWN_SPEAKER,
} from "@app/analytics/MeetingReport/constants";
import useEditSpeakerName from "@app/analytics/MeetingReport/hooks/useEditSpeakerName";
import useSessionMetadata from "@app/analytics/MeetingReport/hooks/useSessionMetadata";
import useTranscriptStore from "@app/analytics/MeetingReport/stores/transcript";
import * as Events from "@app/analytics/MeetingReport/tracking/events";
import {
  EditSpeakerButton,
  EditSpeakerIconButton,
  EditSpeakerTextField,
} from "@app/analytics/MeetingReport/Transcript/styles";
import { getApiSpeakerOperation } from "@app/analytics/MeetingReport/Transcript/utils";

import Avatar from "@rui/Avatar";
import Button from "@rui/Button";
import { Column, Row } from "@rui/Stack";

interface TranscriptEditSpeakerModalProps {
  open: boolean;
  onClose: () => void;
  inEditMode?: boolean;
}

const TranscriptEditSpeakerModal = ({
  open,
  onClose,
}: TranscriptEditSpeakerModalProps) => {
  const {
    data: { sessionId },
  } = useSessionMetadata();
  const allSpeakers = useTranscriptStore((state) => state.allSpeakers);
  const [editingName, setEditingName] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>();

  const { updateSpeaker } = useEditSpeakerName({ sessionId });

  const speakers = useMemo(
    () =>
      reject(
        (speaker) =>
          !speaker.name || SUPPRESSED_SPEAKER_NAMES.includes(speaker.name),
        allSpeakers
      ),
    [allSpeakers]
  );

  const handleEditClick = (name: string | null) => {
    if (!name) return;
    setEditingName(name);
  };

  const handleInputRef = (node: HTMLInputElement | null) => {
    node?.focus();
    inputRef.current = node;
  };

  const handleSave = () => {
    const newName = inputRef.current ? trim(inputRef.current.value) : null;

    if (isEmpty(newName)) return;
    if (editingName && newName === trim(editingName)) {
      setEditingName(null);
      return;
    }

    const operation = getApiSpeakerOperation({
      action: "update",
      newName,
      existingName: editingName ?? UNKNOWN_SPEAKER,
    });

    updateSpeaker.mutate(
      { operation },
      {
        onSuccess: () => setEditingName(null),
      }
    );
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case "Enter":
        event.preventDefault();
        handleSave();
        break;
      default:
        break;
    }
  };

  const handleDelete = (name: string | null) => {
    if (!name) return;

    Events.editSpeaker({ source: "tile" });
    const operation = getApiSpeakerOperation({
      action: "remove",
      existingName: name,
      newName: null,
    });

    updateSpeaker.mutate({ operation });
  };

  const handleClose = () => {
    setEditingName(null);
    onClose?.();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Column
        padding="20px 24px 16px 24px"
        spacing="12px"
        width="400px"
        maxWidth="100%"
      >
        <Row variant="apart">
          <Typography variant="h2">Edit Speakers</Typography>
          <ModalCloseButton onClick={handleClose} />
        </Row>
        <Column spacing="4px">
          {speakers.map((speaker) => (
            <Row
              key={speaker.name}
              variant="apart"
              spacing="10px"
              padding="4px"
            >
              <Avatar name={speaker.name ?? ""} />
              {editingName === speaker.name ? (
                <>
                  <EditSpeakerTextField
                    inputRef={handleInputRef}
                    defaultValue={speaker.name}
                    placeholder="Enter a name..."
                    inputProps={{ maxLength: 100 }}
                    onKeyDown={handleKeyDown}
                  />
                  <EditSpeakerButton
                    variant="contained"
                    onClick={handleSave}
                    loading={updateSpeaker.isPending}
                  >
                    Save
                  </EditSpeakerButton>
                </>
              ) : (
                <>
                  <Typography
                    flexGrow={1}
                    variant="bodySecondaryReg"
                    color="sahdes.neutral.800"
                  >
                    {speaker.name}
                  </Typography>
                  <EditSpeakerIconButton
                    onClick={() => handleDelete(speaker.name)}
                  >
                    <DeleteIcon />
                  </EditSpeakerIconButton>
                  <EditSpeakerIconButton
                    disabled={Boolean(editingName)}
                    onClick={() => handleEditClick(speaker.name)}
                  >
                    <EditIcon />
                  </EditSpeakerIconButton>
                </>
              )}
            </Row>
          ))}
        </Column>
        <Divider />
        <Row variant="right">
          <Button
            onClick={handleClose}
            variant="contained"
            sx={{ height: "42px" }}
          >
            Done
          </Button>
        </Row>
      </Column>
    </Modal>
  );
};

export default TranscriptEditSpeakerModal;
